// UK PAYE, employee National Insurance and student loan maths for the UK
// Payslip generator (2025/26 figures). As with the Canadian module, the
// automatic calculation is the default; a flat PAYE or NI percent from the
// form's Tax Settings (or the admin's per-year defaults) replaces that
// line's maths. Each period is taxed on its own (non-cumulative), which is
// what a generated payslip needs.

export const UK_TAX_YEAR = "2025/26";
export const PERSONAL_ALLOWANCE = 12570;
export const ALLOWANCE_TAPER_FROM = 100000; // £1 of allowance lost per £2 above this

// Rest-of-UK bands on taxable income (pay above the allowance)
export const UK_BANDS = [
  { max: 37700, rate: 0.20 },
  { max: 125140, rate: 0.40 },
  { max: Infinity, rate: 0.45 },
];
// Scottish bands (an S-prefixed tax code)
export const SCOTTISH_BANDS = [
  { max: 2827, rate: 0.19 },
  { max: 14921, rate: 0.20 },
  { max: 31092, rate: 0.21 },
  { max: 62430, rate: 0.42 },
  { max: 125140, rate: 0.45 },
  { max: Infinity, rate: 0.48 },
];

// Employee Class 1 National Insurance
export const NI_PRIMARY_THRESHOLD = 12570;
export const NI_UPPER_EARNINGS_LIMIT = 50270;
export const NI_MAIN_RATE = 0.08;
export const NI_UPPER_RATE = 0.02;
export const NI_REDUCED_RATE = 0.0185; // category B: married women's reduced rate

export const NI_CATEGORIES = [
  ["A", "A: standard"],
  ["B", "B: married women's reduced rate"],
  ["C", "C: over State Pension age"],
  ["H", "H: apprentice under 25"],
  ["J", "J: deferred (pays NI in another job)"],
  ["M", "M: under 21"],
  ["V", "V: veteran's first civilian job"],
  ["Z", "Z: under 21, deferred"],
];

export const STUDENT_LOAN_PLANS = [
  ["none", "None"],
  ["plan1", "Plan 1"],
  ["plan2", "Plan 2"],
  ["plan4", "Plan 4 (Scotland)"],
  ["plan5", "Plan 5"],
  ["postgrad", "Postgraduate Loan"],
];
const STUDENT_LOAN = {
  plan1: { threshold: 26065, rate: 0.09 },
  plan2: { threshold: 28470, rate: 0.09 },
  plan4: { threshold: 32745, rate: 0.09 },
  plan5: { threshold: 25000, rate: 0.09 },
  postgrad: { threshold: 21000, rate: 0.06 },
};

export const PERIODS_PER_YEAR = { weekly: 52, biweekly: 26, fourweekly: 13, monthly: 12 };
const periods = (frequency) => PERIODS_PER_YEAR[frequency] || 12;

// A tax code's yearly allowance and treatment. 1257L: £12,570 tax free.
// K475: £4,750 ADDED to taxable pay. BR / D0 / D1 (D2, D3 in Scotland): all
// pay at one rate. NT: no tax. 0T: no allowance. An S prefix means the
// Scottish bands, a C prefix the Welsh (same bands as the rest of the UK).
export function parseTaxCode(code) {
  const c = String(code || "1257L").toUpperCase().replace(/\s+/g, "");
  const scottish = c.startsWith("S");
  const body = c.replace(/^[SC]/, "");
  if (body === "NT") return { scottish, flatRate: 0, allowance: 0 };
  if (body === "BR") return { scottish, flatRate: 0.20, allowance: 0 };
  if (body === "D0") return { scottish, flatRate: scottish ? 0.21 : 0.40, allowance: 0 };
  if (body === "D1") return { scottish, flatRate: scottish ? 0.42 : 0.45, allowance: 0 };
  if (body === "D2") return { scottish, flatRate: 0.45, allowance: 0 };
  if (body === "D3") return { scottish, flatRate: 0.48, allowance: 0 };
  if (body === "0T") return { scottish, flatRate: null, allowance: 0 };
  const k = body.match(/^K(\d+)$/);
  if (k) return { scottish, flatRate: null, allowance: -(parseInt(k[1], 10) * 10) };
  const n = body.match(/^(\d+)[LMNT]?$/);
  if (n) return { scottish, flatRate: null, allowance: parseInt(n[1], 10) * 10 };
  return { scottish, flatRate: null, allowance: PERSONAL_ALLOWANCE };
}

function bandTax(taxable, bands) {
  let tax = 0;
  let lower = 0;
  for (const b of bands) {
    if (taxable <= lower) break;
    const slice = Math.min(taxable, b.max) - lower;
    if (slice > 0) tax += slice * b.rate;
    lower = b.max;
  }
  return tax;
}

// Income tax for a full year of this income under the tax code
export function annualPaye(annualIncome, taxCode) {
  const { scottish, flatRate, allowance } = parseTaxCode(taxCode);
  if (flatRate !== null) return annualIncome * flatRate;
  let allow = allowance;
  if (allow > 0 && annualIncome > ALLOWANCE_TAPER_FROM) {
    allow = Math.max(0, allow - (annualIncome - ALLOWANCE_TAPER_FROM) / 2);
  }
  const taxable = Math.max(0, annualIncome - allow);
  return bandTax(taxable, scottish ? SCOTTISH_BANDS : UK_BANDS);
}

// Employee NI for one period: the main rate between the primary threshold
// and the upper earnings limit (both divided across the year's periods),
// the upper rate above; the category letter picks the main rate
export function periodNationalInsurance(grossPay, payFrequency, category = "A") {
  const n = periods(payFrequency);
  const cat = String(category || "A").toUpperCase();
  if (cat === "C" || cat === "X") return 0;
  const pt = NI_PRIMARY_THRESHOLD / n;
  const uel = NI_UPPER_EARNINGS_LIMIT / n;
  const mainRate = (cat === "B" || cat === "E" || cat === "T") ? NI_REDUCED_RATE
    : (cat === "J" || cat === "Z" || cat === "Q") ? NI_UPPER_RATE
    : NI_MAIN_RATE;
  const band1 = Math.max(0, Math.min(grossPay, uel) - pt);
  const band2 = Math.max(0, grossPay - uel);
  return band1 * mainRate + band2 * NI_UPPER_RATE;
}

// Student loan for one period: the plan's rate on pay over its threshold
// (divided across the year's periods), rounded down to the pound as HMRC does
export function periodStudentLoan(grossPay, payFrequency, plan) {
  const p = STUDENT_LOAN[plan];
  if (!p) return 0;
  const over = grossPay - p.threshold / periods(payFrequency);
  return over > 0 ? Math.floor(over * p.rate) : 0;
}

// The deductions on one payslip. `overrides`: { paye, ni } as fractions
// (0.2 = 20%); null or undefined means the automatic maths for that line.
export function calculateUkTaxes(grossPay, payFrequency, { taxCode = "1257L", niCategory = "A", studentLoanPlan = "none", overrides = {} } = {}) {
  const n = periods(payFrequency);
  const paye = overrides && overrides.paye != null ? grossPay * overrides.paye : annualPaye(grossPay * n, taxCode) / n;
  const ni = overrides && overrides.ni != null ? grossPay * overrides.ni : periodNationalInsurance(grossPay, payFrequency, niCategory);
  const loan = periodStudentLoan(grossPay, payFrequency, studentLoanPlan);
  const r = (x) => Math.round(Math.max(0, x) * 100) / 100;
  const out = { payeTax: r(paye), nationalInsurance: r(ni), studentLoan: r(loan) };
  out.totalTax = r(out.payeTax + out.nationalInsurance + out.studentLoan);
  return out;
}

// NI number: two letters, six digits, one letter, written AB 12 34 56 C
export function formatNiNumber(value) {
  const c = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 9);
  return [c.slice(0, 2), c.slice(2, 4), c.slice(4, 6), c.slice(6, 8), c.slice(8, 9)].filter(Boolean).join(" ");
}
export function validateNiNumber(value) {
  const c = String(value || "").toUpperCase().replace(/\s/g, "");
  if (!/^[A-CEGHJ-PR-TW-Z]{2}\d{6}[A-D]$/.test(c)) return { isValid: false, error: "NI number looks like AB 12 34 56 C" };
  return { isValid: true, error: "" };
}

// Postcode: the outward and inward parts with one space (SW1A 1AA)
export function formatPostcode(value) {
  const c = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 7);
  if (c.length <= 3) return c;
  return `${c.slice(0, c.length - 3)} ${c.slice(-3)}`;
}
export function validatePostcode(value) {
  const c = String(value || "").toUpperCase().replace(/\s/g, "");
  if (!/^[A-Z]{1,2}\d[A-Z\d]?\d[A-Z]{2}$/.test(c)) return { isValid: false, error: "Invalid postcode format" };
  return { isValid: true, error: "" };
}
